/**
 * PageLayout — Standard page wrapper for consistent layout and spacing.
 *
 * Provides a full-screen background with a centered, max-width container.
 *
 * @param {React.ReactNode} children
 * @param {'extrawide'|'wide'|'default'|'narrow'|'reading'} size - Container width tier
 * @param {'background'|'surface'|string} bg - Background color (shortcut or arbitrary class).
 *   Default 'background' (Mist) since the 2026-10-01 visual refresh: white is
 *   for frames on the page, not the page itself.
 * @param {string} py - Vertical padding Tailwind class (default: 'py-8')
 * @param {string} className - Extra classes on the outer wrapper
 * @param {string} containerClassName - Extra classes on the inner container
 * @param {'main'|'div'} as - HTML element for the container (default: 'main')
 */

import React from 'react';

const SIZE_CLASSES = {
  extrawide: 'max-w-[1536px]', // 1536px — 5-column card grids (saved items, social feed)
  wide:      'max-w-7xl',      // 1280px — card grids, listing pages
  default:   'max-w-6xl',      // 1152px — standard content, profiles
  narrow:    'max-w-5xl',      // 1024px — feeds, form-heavy pages
  reading:   'max-w-4xl',      // 896px  — detail pages, articles
};

const BG_SHORTCUTS = {
  background: 'bg-background',
  surface:    'bg-surface',
};

function PageLayout({
  children,
  size = 'default',
  bg = 'background',
  py = 'py-8',
  className = '',
  containerClassName = '',
  as: Container = 'main',
}) {
  const bgClass = BG_SHORTCUTS[bg] || bg;
  const maxW = SIZE_CLASSES[size] || SIZE_CLASSES.default;

  return (
    <div className={`min-h-dvh ${bgClass} ${className}`}>
      <Container className={`${maxW} mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 ${py} ${containerClassName}`}>
        {children}
      </Container>
    </div>
  );
}

export default PageLayout;
